/**
 * Shared Quiz Page
 *
 * Displays a published quiz that someone has shared.
 * Users can view details and clone the quiz to their local library.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { parseShareableSlug } from '@/utils/slugify';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AppHeader } from '@/components/layout/AppHeader';
import { PageHeader } from '@/components/layout/PageHeader';
import { db } from '@/db/database';
import type { QuizConfig, Category } from '@/db/types';
import { Play, Copy, Calendar, ListOrdered } from 'lucide-react';
import { toast } from 'sonner';
import type { Database } from '@/lib/supabase-types';
import { getQuizFlag } from '@/utils/quizMetadata';
import { BookMascot } from '@/components/ui/BookMascot';

type SharedQuiz = Database['public']['Tables']['shared_quizzes']['Row'];

export function SharedQuizPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<SharedQuiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    if (!slug) {
      setError('Invalid quiz URL');
      setLoading(false);
      return;
    }

    loadSharedQuiz();
  }, [slug]);

  const loadSharedQuiz = async () => {
    if (!slug) return;

    try {
      setLoading(true);
      setError(null);

      // Parse the shareable slug to extract short_id
      const { shortId } = parseShareableSlug(slug);

      // Fetch quiz from Supabase
      const { data, error: fetchError } = await supabase
        .from('shared_quizzes')
        .select('*')
        .eq('short_id', shortId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // Not found
          setError('Quiz not found. It may have been removed.');
        } else {
          throw fetchError;
        }
        return;
      }

      setQuiz(data);

      // Increment view count (fire and forget)
      supabase
        .from('shared_quizzes')
        .update({ view_count: (data.view_count || 0) + 1 } as any)
        .eq('id', data.id)
        .then(() => {
          console.log('View count incremented');
        })
        .catch((err: any) => {
          console.warn('Failed to increment view count:', err);
        });

    } catch (err) {
      console.error('Failed to load shared quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleCloneAndPlay = async () => {
    if (!quiz) return;

    setCloning(true);

    try {
      // Convert shared quiz to local QuizConfig format
      const quizConfig: QuizConfig = {
        signature: `shared-${quiz.id}`,
        title: quiz.title,
        description: quiz.description || '',
        category: quiz.category as Category,
        geography: quiz.dataset_label.split('·')[0]?.trim() || '',
        mapQuizId: null,
        mapQuizName: null,
        datasetLabel: quiz.dataset_label,
        datasetSignature: `shared-${quiz.id}`,
        datasetSource: quiz.dataset_source as any,
        storedCollection: quiz.stored_collection || undefined,
        promptKeys: quiz.prompt_keys,
        filters: quiz.filters as any[],
        excludedFeatureIds: quiz.excluded_feature_ids,
        scopeKey: quiz.scope_key || null,
        scopeBias: quiz.scope_bias,
        repetitions: quiz.repetitions,
        maxAttemptsPerPrompt: quiz.max_attempts_per_prompt,
        eligibleCount: quiz.eligible_count,
        totalFeatures: quiz.total_features,
        granularityLevel: quiz.granularity_level,
        isPublished: false,
        createdAt: new Date().toISOString(),
        lastUsedAt: new Date().toISOString(),
      };

      // Save to local database
      await db.quiz_configs.put(quizConfig);

      // Increment clone count (fire and forget)
      supabase
        .from('shared_quizzes')
        .update({ clone_count: (quiz.clone_count || 0) + 1 } as any)
        .eq('id', quiz.id)
        .then(() => {
          console.log('Clone count incremented');
        })
        .catch((err: any) => {
          console.warn('Failed to increment clone count:', err);
        });

      toast.success('Quiz cloned!', {
        description: 'Quiz saved to your library. Launching...',
      });

      // Navigate to quiz play page
      setTimeout(() => {
        navigate(`/quiz/play?signature=${quizConfig.signature}`);
      }, 500);

    } catch (err) {
      console.error('Failed to clone quiz:', err);
      toast.error('Failed to clone quiz', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setCloning(false);
    }
  };

  const getCategoryInfo = (category: string) => {
    switch (category) {
      case 'phone':
        return { emoji: '📞', label: 'Phone Area Codes', color: 'bg-blue-500/10 text-blue-700' };
      case 'cities':
        return { emoji: '🏙️', label: 'Cities', color: 'bg-purple-500/10 text-purple-700' };
      case 'admin':
        return { emoji: '🗺️', label: 'Administrative', color: 'bg-green-500/10 text-green-700' };
      default:
        return { emoji: '📁', label: 'Custom', color: 'bg-gray-500/10 text-gray-700' };
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader />
        <PageHeader title="Shared Quiz" subtitle="Loading quiz details..." />
        <div className="flex-1 container mx-auto p-6 flex items-center justify-center">
          <div className="text-center space-y-4">
            <BookMascot size="lg" />
            <p className="text-muted-foreground">Loading quiz...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !quiz) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader />
        <PageHeader title="Shared Quiz" subtitle="Quiz not found" />
        <div className="flex-1 container mx-auto p-6 flex items-center justify-center">
          <Card className="max-w-md w-full border-destructive">
            <CardContent className="p-12 text-center space-y-4">
              <div className="text-6xl">❌</div>
              <h2 className="text-2xl font-semibold text-destructive">Quiz Not Found</h2>
              <p className="text-muted-foreground">
                {error || 'This quiz may have been removed or the link is invalid.'}
              </p>
              <Button onClick={() => navigate('/')}>
                Go Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(quiz.category);
  const createdDate = new Date(quiz.created_at);

  // Extract flag from quiz data
  const quizConfigForFlag = {
    datasetLabel: quiz.dataset_label,
    datasetSource: quiz.dataset_source,
  } as QuizConfig;
  const flag = getQuizFlag(quizConfigForFlag);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />
      <PageHeader
        title={`${flag} ${quiz.title}`}
        subtitle={quiz.description || `Shared quiz from the MapQuizzr community`}
        actions={
          <Badge variant="secondary" className={categoryInfo.color}>
            {categoryInfo.emoji} {categoryInfo.label}
          </Badge>
        }
      />

      <div className="flex-1 container mx-auto p-6 max-w-3xl">
        <Card className="overflow-hidden">
          {/* Main Action */}
          <div className="p-6 bg-gradient-to-br from-primary/10 to-accent/10 border-b">
            <Button
              size="lg"
              className="w-full"
              onClick={handleCloneAndPlay}
              disabled={cloning}
            >
              <Play className="w-5 h-5 mr-2" />
              {cloning ? 'Cloning...' : 'Clone & Play Quiz'}
            </Button>
          </div>

          <CardContent className="p-8 space-y-6">
            {/* Quiz Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-primary">{quiz.eligible_count}</div>
                <div className="text-xs text-muted-foreground mt-1">Questions</div>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-primary">{quiz.view_count || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Views</div>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-primary">{quiz.clone_count || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Clones</div>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-primary">{quiz.max_attempts_per_prompt}</div>
                <div className="text-xs text-muted-foreground mt-1">Max Attempts</div>
              </div>
            </div>

            {/* Quiz Details */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase">Quiz Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                  <ListOrdered className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Region:</span>
                  <span className="font-medium">{quiz.dataset_label}</span>
                </div>
                {quiz.scope_key && (
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                    <ListOrdered className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Scope:</span>
                    <span className="font-medium">{quiz.scope_key}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Published:</span>
                  <span className="font-medium">
                    {createdDate.toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Prompt Keys Preview */}
            {quiz.prompt_keys && quiz.prompt_keys.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase">
                  What You'll Learn
                </h3>
                <div className="flex flex-wrap gap-2">
                  {quiz.prompt_keys.map((key) => (
                    <Badge key={key} variant="outline">
                      {key}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Share Section */}
            <div className="pt-4 border-t space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase">Share This Quiz</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={window.location.href}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm bg-muted border rounded-md font-mono"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link copied to clipboard!');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={() => navigate('/')}>
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
